/**
 * InstallPwaPrompt — surfaces a friendly "Install [School] app" call-to-action
 * on the public school landing page and on the parent dashboard.
 *
 * Behaviour by platform:
 *  - Modern Android/Chromium/Edge: captures the `beforeinstallprompt` event,
 *    calls .prompt() when the button is tapped.
 *  - iOS Safari: never fires beforeinstallprompt. We detect iOS and surface a
 *    short illustrated tutorial ("Tap Share → Add to Home Screen").
 *  - Already installed (display-mode standalone OR navigator.standalone): the
 *    component renders nothing.
 *  - Browsers without ServiceWorker: hidden.
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Download, Share, PlusSquare, Smartphone, X } from "lucide-react";

function detectIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  return !!window.navigator.standalone;
}

function dismissKey(slug) { return `sb_pwa_dismissed_${slug || "platform"}`; }

export default function InstallPwaPrompt({
  slug,
  schoolName = "this school",
  palette = null,
  variant = "banner", // "banner" | "compact"
}) {
  const [deferred, setDeferred] = useState(null);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [ios] = useState(detectIos);

  useEffect(() => {
    setInstalled(isStandalone());
    try { setDismissed(localStorage.getItem(dismissKey(slug)) === "1"); } catch { /* ignore */ }

    function onPrompt(e) { e.preventDefault(); setDeferred(e); }
    function onInstalled() {
      setInstalled(true);
      setDeferred(null);
      try { localStorage.removeItem(dismissKey(slug)); } catch { /* ignore */ }
    }
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [slug]);

  if (installed) return null;
  if (dismissed) return null;
  // Hide entirely on platforms without service worker support.
  if (typeof navigator !== "undefined" && !("serviceWorker" in navigator)) return null;
  // Nothing to offer on non-iOS browsers that never fired beforeinstallprompt.
  if (!ios && !deferred) return null;

  async function clickInstall() {
    if (ios) { setShowIosSheet(true); return; }
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") setInstalled(true);
      } catch { /* user cancelled */ }
      setDeferred(null);
    }
  }

  function dismiss() {
    setDismissed(true);
    try { localStorage.setItem(dismissKey(slug), "1"); } catch { /* ignore */ }
  }

  const accent = palette?.text || "#1D4ED8";
  const gradFrom = palette?.from || "#3B82F6";
  const gradTo = palette?.to || "#1D4ED8";

  if (variant === "compact") {
    return (
      <Button
        onClick={clickInstall}
        size="sm"
        className="rounded-full text-xs text-white"
        style={{ background: `linear-gradient(90deg, ${gradFrom}, ${gradTo})` }}
        data-testid="install-pwa-compact-btn"
      >
        <Download className="w-3.5 h-3.5 mr-1.5" /> Install app
      </Button>
    );
  }

  return (
    <>
      <div
        className="relative rounded-2xl border bg-white shadow-floating p-4 flex items-center gap-3"
        style={{ borderColor: `${accent}25` }}
        data-testid="install-pwa-banner"
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 rounded-full p-1.5 hover:bg-slate-100 transition-colors"
          aria-label="Dismiss install prompt"
          data-testid="install-pwa-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }}
        >
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="font-heading font-semibold text-foreground">Install {schoolName} app</div>
          <p className="text-xs text-slate-500 mt-0.5 leading-snug">
            Get a one-tap shortcut on your home screen plus push notifications for attendance, payments, and announcements.
          </p>
        </div>
        <Button
          onClick={clickInstall}
          className="rounded-full text-white px-4 shrink-0"
          style={{ background: `linear-gradient(90deg, ${gradFrom}, ${gradTo})` }}
          data-testid="install-pwa-btn"
        >
          <Download className="w-4 h-4 mr-1.5" /> Install
        </Button>
      </div>

      <Dialog open={showIosSheet} onOpenChange={setShowIosSheet}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="install-pwa-ios-sheet">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Install on iPhone / iPad</DialogTitle>
            <DialogDescription>Two taps and you have a {schoolName} app icon on your home screen.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-2">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#EFF6FF] text-[#1D4ED8] font-heading font-bold text-xs flex items-center justify-center shrink-0">1</span>
              <div className="text-sm text-foreground">
                Tap the <Share className="inline w-4 h-4 -mt-0.5" style={{ color: accent }} /> <strong>Share</strong> button in Safari (bottom bar).
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#EFF6FF] text-[#1D4ED8] font-heading font-bold text-xs flex items-center justify-center shrink-0">2</span>
              <div className="text-sm text-foreground">
                Scroll down and pick <PlusSquare className="inline w-4 h-4 -mt-0.5" style={{ color: accent }} /> <strong>Add to Home Screen</strong>.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#EFF6FF] text-[#1D4ED8] font-heading font-bold text-xs flex items-center justify-center shrink-0">3</span>
              <div className="text-sm text-foreground">
                Tap <strong>Add</strong> on the top-right. The {schoolName} icon will appear next to your other apps.
              </div>
            </li>
          </ol>
          <div className="mt-3 text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2">
            Once installed, launching from the home-screen icon hides Safari&apos;s toolbar so SchoolBoss feels like a native app.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
